import type { ReactNode } from 'react'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ScoreBar } from '../shared/charts'
import { StatusPill } from '../shared/pills'
import { useAdminOverview } from './admin-overview-provider'
import { derivePace } from './derive'

export function PaceVsPlan(): ReactNode {
  const { overview, isLoading } = useAdminOverview()
  const rows = derivePace(
    overview?.completionByTrack ?? [],
    overview?.throughput ?? [],
  )
  const behind = rows.some((row) => !row.onPace)

  return (
    <Card className="h-full gap-4 border-(--cd-border) bg-(--cd-card) py-4 text-(--cd-text) shadow-none">
      <CardHeader className="gap-1 px-4">
        <CardTitle className="text-sm">Pace vs. plan</CardTitle>
        <CardDescription className="text-xs text-(--cd-text-muted)">
          Completed vs. scheduled homework per week
        </CardDescription>
        {rows.length > 0 ? (
          <CardAction>
            <StatusPill tone={behind ? 'amber' : 'green'}>
              {behind ? 'Off pace' : 'On pace'}
            </StatusPill>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            Loading pacing data…
          </p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            No pacing data yet — it appears once homework is scheduled.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {rows.map((row) => (
                <div key={row.weekNumber} className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-xs font-semibold">
                      Week {row.weekNumber}
                    </span>
                    <span className="font-mono text-[11px] text-(--cd-text-muted)">
                      {row.completed} vs {row.planned} planned
                    </span>
                  </div>
                  <ScoreBar
                    percent={row.percent}
                    marker={100}
                    color={row.onPace ? 'var(--cd-green)' : 'var(--cd-red)'}
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-(--cd-text-faint)">
              Vertical marker = fully on plan (100% of scheduled homework)
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}
